import React, { Component } from 'react';
import axiosInstance from '../../main/axios';
import { toastr } from 'react-redux-toastr';
import MapaGoogle from '../../mapa/mapaGoogle';
import { Marker, InfoWindow } from 'react-google-maps';

const PIN_VIATURA = 'https://maps.google.com/mapfiles/ms/icons/truck.png';
const PIN_VIATURA_OFF = 'https://maps.google.com/mapfiles/ms/icons/grey-dot.png';

function parseCoord(value) {
  if ((value === null || value === undefined) || value === '') return NaN;
  if (typeof value === 'number') return value;
  const normalized = String(value).trim().replace(',', '.');
  return parseFloat(normalized);
}

function centroValido(centro) {
  const lat = parseCoord(centro && centro.latitude);
  const lng = parseCoord(centro && centro.longitude);
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return false;
  if (Math.abs(lat) <= 1 && Math.abs(lng) <= 1) return false;
  return true;
}

function normalizarViaturas(viaturas) {
  return (viaturas || [])
    .map(viatura => {
      const latitude = parseCoord(viatura.ultima_lat);
      const longitude = parseCoord(viatura.ultima_lng);
      const temPosicao = Number.isFinite(latitude) && Number.isFinite(longitude);
      return { ...viatura, latitude, longitude, temPosicao };
    })
    .filter(v => v.ativo !== 0 && v.ativo !== false);
}

function formatarDataPosicao(viatura) {
  if (!viatura.ultima_posicao_em) return null;
  return new Date(viatura.ultima_posicao_em).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

class MapaViaturas extends Component {
  constructor(props) {
    super(props);
    this.state = {
      viaturas: [],
      centro: null,
      centroFallback: null,
      opcoes: { equipes: [] },
      filtroEquipe: '',
      loading: false,
      googleMapsReady: typeof window !== 'undefined' && !!(window.google && window.google.maps),
      markerAberto: null,
    };
    this.mapRef = null;
    this.carregarOpcoes = this.carregarOpcoes.bind(this);
    this.carregarViaturas = this.carregarViaturas.bind(this);
    this.carregarCentroFallback = this.carregarCentroFallback.bind(this);
    this.handleFiltroEquipe = this.handleFiltroEquipe.bind(this);
    this.abrirMarker = this.abrirMarker.bind(this);
    this.fecharMarker = this.fecharMarker.bind(this);
    this.onMapLoad = this.onMapLoad.bind(this);
    this.ajustarMapaAosPontos = this.ajustarMapaAosPontos.bind(this);
    this.renderMarkers = this.renderMarkers.bind(this);
    this.renderInfoWindow = this.renderInfoWindow.bind(this);
  }

  componentDidMount() {
    this.carregarOpcoes();
    this.carregarViaturas();

    if (!this.state.googleMapsReady) {
      this.googleMapsReadyInterval = setInterval(() => {
        if (typeof window !== 'undefined' && window.google && window.google.maps) {
          clearInterval(this.googleMapsReadyInterval);
          this.googleMapsReadyInterval = null;
          this.setState({ googleMapsReady: true });
        }
      }, 300);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.viaturas !== this.state.viaturas && this.mapRef) {
      this.ajustarMapaAosPontos(this.mapRef);
    }
  }

  componentWillUnmount() {
    if (this.googleMapsReadyInterval) {
      clearInterval(this.googleMapsReadyInterval);
    }
  }

  carregarOpcoes() {
    axiosInstance
      .get('/manutencao/opcoes')
      .then(resp => {
        this.setState({ opcoes: { equipes: resp.data.equipes || [] } });
      })
      .catch(() => toastr.error('Erro', 'Não foi possível carregar os filtros.'));
  }

  carregarCentroFallback() {
    return axiosInstance.get('/fasesgeral/mapa/inicio/0').then(resp => {
      const data = resp.data || {};
      if (centroValido(data)) {
        this.setState({ centroFallback: data });
      } else {
        this.setState({ centroFallback: null });
      }
    });
  }

  carregarViaturas() {
    this.setState({ loading: true, markerAberto: null });
    axiosInstance
      .get('/manutencao/viaturas')
      .then(resp => {
        const viaturas = normalizarViaturas(resp.data);
        const comPosicao = viaturas.filter(v => v.temPosicao);
        const primeira = comPosicao[0];
        const centro = primeira
          ? { latitude: primeira.latitude, longitude: primeira.longitude }
          : null;

        this.setState({ viaturas, centro, loading: false }, () => {
          if (this.mapRef) {
            this.ajustarMapaAosPontos(this.mapRef);
          }
        });

        if (!centroValido(centro)) {
          return this.carregarCentroFallback();
        }
        return null;
      })
      .catch(() => {
        toastr.error('Erro', 'Não foi possível carregar as viaturas.');
        this.setState({ loading: false });
        return this.carregarCentroFallback();
      });
  }

  handleFiltroEquipe(e) {
    this.setState({ filtroEquipe: e.target.value, markerAberto: null }, () => {
      if (this.mapRef) {
        this.ajustarMapaAosPontos(this.mapRef);
      }
    });
  }

  getViaturasFiltradas() {
    const { viaturas, filtroEquipe } = this.state;
    if (!filtroEquipe) return viaturas;
    const equipeId = parseInt(filtroEquipe, 10);
    return viaturas.filter(v => v.equipe_id === equipeId);
  }

  getCentroExibicao() {
    const { centro, centroFallback } = this.state;
    if (centroValido(centro)) return centro;
    if (centroValido(centroFallback)) return centroFallback;
    return null;
  }

  onMapLoad(map) {
    this.mapRef = map;
    this.ajustarMapaAosPontos(map);
  }

  ajustarMapaAosPontos(map) {
    if (!map || !window.google || !window.google.maps) return;

    const comPosicao = this.getViaturasFiltradas().filter(v => v.temPosicao);
    if (comPosicao.length === 0) return;

    if (comPosicao.length === 1) {
      // react-google-maps expõe panTo/fitBounds no ref, mas não setZoom
      map.panTo({ lat: comPosicao[0].latitude, lng: comPosicao[0].longitude });
      return;
    }

    const bounds = new window.google.maps.LatLngBounds();
    comPosicao.forEach(v => bounds.extend({ lat: v.latitude, lng: v.longitude }));
    map.fitBounds(bounds);
  }

  abrirMarker(viaturaId) {
    this.setState({ markerAberto: viaturaId });
  }

  fecharMarker() {
    this.setState({ markerAberto: null });
  }

  renderInfoWindow(viatura) {
    const dataPosicao = formatarDataPosicao(viatura);

    return (
      <InfoWindow onCloseClick={this.fecharMarker}>
        <div className="mapa-viaturas-info-window">
          <div className="mapa-viaturas-info-header">
            <strong>{viatura.placa}</strong>
            {viatura.descricao ? <span>{viatura.descricao}</span> : null}
          </div>
          <div className="mapa-viaturas-info-row">
            <span>Equipe</span>
            <strong>{viatura.equipe_nome || '—'}</strong>
          </div>
          {dataPosicao ? (
            <div className="mapa-viaturas-info-row">
              <span>Última posição</span>
              <strong>{dataPosicao}</strong>
            </div>
          ) : null}
          {viatura.temPosicao ? (
            <div className="mapa-viaturas-info-row">
              <span>Coordenadas</span>
              <strong>
                {viatura.latitude.toFixed(5)}, {viatura.longitude.toFixed(5)}
              </strong>
            </div>
          ) : (
            <p className="mapa-viaturas-info-sem-gps">
              <i className="fa fa-satellite-dish" /> Aguardando fonte GPS
            </p>
          )}
        </div>
      </InfoWindow>
    );
  }

  renderMarkers() {
    const { markerAberto } = this.state;
    const viaturas = this.getViaturasFiltradas();

    return viaturas.map(viatura => {
      if (!viatura.temPosicao) return null;

      const position = { lat: viatura.latitude, lng: viatura.longitude };

      return (
        <Marker
          key={viatura.id}
          position={position}
          icon={viatura.ultima_posicao_em ? PIN_VIATURA : PIN_VIATURA_OFF}
          onClick={() => this.abrirMarker(viatura.id)}
        >
          {markerAberto === viatura.id && this.renderInfoWindow(viatura)}
        </Marker>
      );
    });
  }

  renderMapa() {
    const { googleMapsReady, loading } = this.state;
    const centroExibicao = this.getCentroExibicao();
    const viaturas = this.getViaturasFiltradas();
    const comPosicao = viaturas.filter(v => v.temPosicao);

    if (!googleMapsReady) {
      return (
        <div className="mapa-fases-premium-empty mapa-viaturas-empty">
          <div>
            <i className="fa fa-spinner fa-spin" />
            <h4>Carregando Google Maps</h4>
            <p>Aguarde enquanto o mapa é preparado.</p>
          </div>
        </div>
      );
    }

    if (!centroExibicao) {
      return (
        <div className="mapa-fases-premium-empty mapa-viaturas-empty">
          <div>
            <i className="fa fa-map-marked-alt" />
            <h4>Sem referência de mapa</h4>
            <p>Cadastre viaturas ou configure coordenadas no sistema para exibir o mapa.</p>
          </div>
        </div>
      );
    }

    return (
      <>
        {loading && (
          <div className="mapa-os-premium-loading">
            <i className="fa fa-spinner fa-spin" />
          </div>
        )}
        {comPosicao.length === 0 && !loading && (
          <div className="mapa-viaturas-gps-banner">
            <i className="fa fa-satellite-dish" />
            <div>
              <strong>Aguardando fonte GPS</strong>
              <p>
                {viaturas.length} viatura(s) cadastrada(s), nenhuma com posição registrada. Quando a integração GPS
                estiver ativa, as viaturas aparecerão automaticamente neste mapa.
              </p>
            </div>
          </div>
        )}
        <MapaGoogle
          containerElement={<div style={{ height: '100%', width: '100%' }} />}
          mapElement={<div style={{ height: '100%' }} />}
          defaultCenter={{
            lat: parseCoord(centroExibicao.latitude),
            lng: parseCoord(centroExibicao.longitude),
          }}
          defaultZoom={14}
          onMapLoad={this.onMapLoad}
        >
          {this.renderMarkers()}
        </MapaGoogle>
      </>
    );
  }

  render() {
    const { opcoes, filtroEquipe } = this.state;
    const viaturas = this.getViaturasFiltradas();
    const comPosicao = viaturas.filter(v => v.temPosicao);

    return (
      <div className="mapa-fases-premium mapa-os-premium mapa-viaturas-premium">
        <div className="mapa-fases-premium-toolbar">
          <div className="mapa-viaturas-toolbar-inner">
            <div>
              <h2>Mapa de Viaturas</h2>
              <p>
                {comPosicao.length} viatura(s) no mapa · {viaturas.length} cadastrada(s)
              </p>
            </div>

            <div className="mapa-fases-premium-controls mapa-viaturas-controls">
              <div className="mapa-fases-premium-filter">
                <span className="mapa-fases-premium-label">Equipe</span>
                <select
                  className="form-control input-sm"
                  value={filtroEquipe}
                  onChange={this.handleFiltroEquipe}
                >
                  <option value="">Todas</option>
                  {opcoes.equipes.map(e => (
                    <option key={e.id} value={e.id}>
                      {e.nome}
                    </option>
                  ))}
                </select>
              </div>

              <button
                type="button"
                className="btn btn-default btn-sm mapa-os-refresh-btn"
                onClick={this.carregarViaturas}
                title="Atualizar"
                aria-label="Atualizar"
              >
                <i className="fa fa-sync-alt" />
              </button>
            </div>
          </div>
        </div>

        <div className="mapa-fases-premium-content" style={{ padding: '0 10px', margin: 0 }}>
          <div
            className="mapa-fases-premium-layout"
            style={{
              display: 'flex',
              height: 'calc(100vh - 100px)',
              gap: '15px',
            }}
          >
            <div className="mapa-fases-premium-map-card mapa-os-premium-map-card">{this.renderMapa()}</div>
          </div>
        </div>
      </div>
    );
  }
}

export default MapaViaturas;
